import React, { useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, UserCircle, X } from 'lucide-react';
import { useAuth } from '@providers/authProvider';
import ThemeToggle from './ThemeToggle';
import { Dropdown, DropdownItem } from './elements/Dropdown';
import BrickLinkApiCounter from './BrickLinkApiCounter';
import { joinClassNames } from './public/classNames';
import './LoggedInHeader.css';

const HEADER_BASE_CLASSES = 'sticky top-0 z-50 w-full border-b border-border-light bg-white/[0.86] backdrop-blur dark:bg-surface-light dark:border-border-light';
const NAV_LINK_BASE_CLASSES = 'logged-in-header__nav-link font-semibold transition-colors';
const NAV_LINK_ACTIVE_CLASSES = 'text-primary dark:text-primary';
const NAV_LINK_INACTIVE_CLASSES = 'text-muted-foreground hover:text-foreground dark:text-muted-foreground dark:hover:text-foreground';
const DROPDOWN_TRIGGER_BASE_CLASSES = 'logged-in-header__nav-trigger inline-flex items-center gap-1 font-semibold transition-colors';

const ENVIRONMENT_BANNERS = {
    dev_test: {
        label: 'DEV TEST ENVIRONMENT',
        className: 'bg-yellow-500'
    },
    staging: {
        label: 'STAGING ENVIRONMENT',
        className: 'bg-purple-500'
    }
};

export const hasLoggedInHeaderEnvironmentBanner = (environmentName) => {
    return Object.prototype.hasOwnProperty.call(ENVIRONMENT_BANNERS, environmentName);
};

const PRIMARY_NAV_LINKS = [
    {
        id: 'home',
        label: 'Home',
        to: '/app/home',
        end: true
    },
    {
        id: 'catalog',
        label: 'Catalog',
        to: '/app/catalog',
        end: false
    },
    {
        id: 'batches',
        label: 'Batches',
        to: '/app/batches',
        end: false
    },
    {
        id: 'orders',
        label: 'Orders',
        to: '/app/orders',
        end: false
    },
    {
        id: 'buy-lego',
        label: 'Buy LEGO',
        to: '/app/buy-lego',
        end: false
    }
];

const DROPDOWN_NAV_GROUPS = [
    {
        id: 'inventory',
        label: 'My Inventory',
        items: [
            { label: 'Inventory', to: '/app/inventory' },
            { label: 'Inventory Locations', to: '/app/inventory-locations' },
            { label: 'Audit', to: '/app/inventory-audit' },
            { label: 'Sources', to: '/app/sources' },
            { label: 'Stow Inventory', to: '/app/stow-inventory' },
            { label: 'Redistribution', to: '/app/redistribution' },
            { label: 'Container Types', to: '/app/container-types' }
        ]
    },
    {
        id: 'sets',
        label: 'My Sets',
        items: [
            { label: 'Sets', to: '/app/userSets' },
            { label: 'Minifigs', to: '/app/minifigs' },
            { label: 'Pick List', to: '/app/pick-list' }
        ]
    }
];

const USER_MENU_ITEMS = [
    { label: 'Profile', to: '/app/profile' },
    { label: 'Settings', to: '/app/settings' }
];

const ADMIN_MENU_ITEMS = [
    { label: 'API Testing', to: '/app/api-testing' },
    { label: 'Audit Logs', to: '/app/audit-logs' },
    { label: 'Feature Flags', to: '/app/feature-flags' },
    { label: 'Site Banners', to: '/app/site-banners' }
];

const getNavLinkClasses = (isActive) => joinClassNames(
    NAV_LINK_BASE_CLASSES,
    isActive ? NAV_LINK_ACTIVE_CLASSES : NAV_LINK_INACTIVE_CLASSES
);

const isRouteActive = (pathname, item) => pathname === item.to || pathname.startsWith(`${item.to}/`);

const isDropdownGroupActive = (pathname, group) => {
    return group.items.some((item) => isRouteActive(pathname, item));
};

const BrandMark = () => (
    <span
        className="logged-in-header__brand-mark inline-flex h-8 w-8 items-center justify-center rounded-[10px] bg-primary text-white shadow-button"
        data-testid="logged-in-header-brand-mark"
        aria-hidden="true"
    >
        <svg className="logged-in-header__brand-icon h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M12 3L4.5 7.25L12 11.5L19.5 7.25L12 3Z" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4.5 7.25V16.75L12 21V11.5L4.5 7.25Z" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M19.5 7.25V16.75L12 21V11.5L19.5 7.25Z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    </span>
);

const LoggedInHeader = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout, hasRole } = useAuth();
    const isAdmin = hasRole('admin');
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const userMenuAnchorRef = useRef(null);

    const environmentBanner = ENVIRONMENT_BANNERS[process.env.REACT_APP_BB_ENV];
    // The phone menu hangs below the header (and the environment banner, when shown),
    // so its height must leave room for both or its last items fall off the screen.
    const mobileMenuMaxHeight = environmentBanner
        ? 'calc(100dvh - var(--header-height) - 2.5rem)'
        : 'calc(100dvh - var(--header-height))';

    const closeDropdown = () => {
        setActiveDropdown(null);
        setIsMobileMenuOpen(false);
    };

    const toggleDropdown = (dropdownName) => {
        setActiveDropdown((currentDropdown) => currentDropdown === dropdownName ? null : dropdownName);
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen((isOpen) => !isOpen);
    };

    const navigateTo = (to) => {
        navigate(to);
        closeDropdown();
    };

    const handleLogout = async () => {
        await logout();
        navigate('/');
        closeDropdown();
    };

    const mobileMenuLinks = [
        ...USER_MENU_ITEMS,
        ...(isAdmin ? ADMIN_MENU_ITEMS : [])
    ];

    return (
        <>
            {environmentBanner && (
                <div className={`${environmentBanner.className} absolute left-0 right-0 top-0 z-[60] flex items-center justify-center px-6 py-2 text-white`}>
                    <span className="text-lg font-bold">{environmentBanner.label}</span>
                </div>
            )}
            <div
                className={joinClassNames(
                    HEADER_BASE_CLASSES,
                    environmentBanner ? 'mt-10' : ''
                )}
                style={{ height: 'var(--header-height)' }}
                data-testid="logged-in-header"
            >
                <div className="logged-in-header__inner mx-auto flex h-full max-w-7xl items-center justify-between px-6">
                    <Link
                        to="/app/home"
                        className="logged-in-header__brand inline-flex items-center gap-2.5 font-bold text-foreground"
                        data-testid="logged-in-header-brand"
                    >
                        <BrandMark />
                        <span>BrickBuddy</span>
                    </Link>

                    <button
                        type="button"
                        className="logged-in-header__mobile-toggle"
                        onClick={toggleMobileMenu}
                        aria-label="Toggle navigation"
                        aria-expanded={isMobileMenuOpen}
                        data-testid="logged-in-header-mobile-menu-button"
                    >
                        {isMobileMenuOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
                    </button>

                    <nav className="logged-in-header__nav min-w-0" aria-label="Authenticated navigation" data-testid="logged-in-header-nav">
                        <ul className="logged-in-header__nav-list flex h-full items-center" data-testid="logged-in-header-nav-list">
                            {PRIMARY_NAV_LINKS.map((link) => (
                                <li className="flex items-center" key={link.id}>
                                    <NavLink
                                        to={link.to}
                                        end={link.end}
                                        className={({ isActive }) => getNavLinkClasses(isActive)}
                                        data-testid={`logged-in-header-link-${link.id}`}
                                    >
                                        {link.label}
                                    </NavLink>
                                </li>
                            ))}

                            {DROPDOWN_NAV_GROUPS.map((group) => {
                                const isActive = isDropdownGroupActive(location.pathname, group);

                                return (
                                    <li className="relative inline-flex items-center" key={group.id}>
                                        <button
                                            type="button"
                                            onClick={() => toggleDropdown(group.id)}
                                            className={joinClassNames(
                                                DROPDOWN_TRIGGER_BASE_CLASSES,
                                                isActive ? NAV_LINK_ACTIVE_CLASSES : NAV_LINK_INACTIVE_CLASSES
                                            )}
                                            aria-current={isActive ? 'page' : undefined}
                                            aria-expanded={activeDropdown === group.id}
                                            data-testid={`logged-in-header-menu-${group.id}`}
                                        >
                                            {group.label}
                                            <ChevronDown className="logged-in-header__dropdown-icon h-4 w-4" aria-hidden="true" />
                                        </button>
                                        <Dropdown
                                            isOpen={activeDropdown === group.id}
                                            onClose={closeDropdown}
                                            align="center"
                                        >
                                            {group.items.map((item) => (
                                                <DropdownItem
                                                    key={item.to}
                                                    onClick={() => navigateTo(item.to)}
                                                >
                                                    {item.label}
                                                </DropdownItem>
                                            ))}
                                        </Dropdown>
                                    </li>
                                );
                            })}

                            <li data-testid="bricklink-status-container" className="flex items-center gap-2">
                                <BrickLinkApiCounter />
                            </li>

                            <li className="flex items-center" data-testid="logged-in-header-theme-toggle-item">
                                <ThemeToggle />
                            </li>

                            <li ref={userMenuAnchorRef} className="relative inline-flex items-center" data-testid="logged-in-header-user-menu-item">
                                <button
                                    type="button"
                                    onClick={() => toggleDropdown('user')}
                                    className={joinClassNames('logged-in-header__user-trigger inline-flex items-center gap-1 transition-colors', NAV_LINK_INACTIVE_CLASSES)}
                                    data-testid="user-menu-trigger"
                                    aria-expanded={activeDropdown === 'user'}
                                >
                                    <UserCircle className="logged-in-header__user-icon h-8 w-8 text-muted-foreground dark:text-muted-foreground" aria-hidden="true" />
                                    <ChevronDown className="logged-in-header__dropdown-icon h-4 w-4" aria-hidden="true" />
                                </button>
                                <Dropdown
                                    isOpen={activeDropdown === 'user'}
                                    onClose={closeDropdown}
                                    align="right"
                                    portal
                                    anchorRef={userMenuAnchorRef}
                                >
                                    {USER_MENU_ITEMS.map((item) => (
                                        <DropdownItem
                                            key={item.to}
                                            onClick={() => navigateTo(item.to)}
                                        >
                                            {item.label}
                                        </DropdownItem>
                                    ))}

                                    {isAdmin && ADMIN_MENU_ITEMS.map((item) => (
                                        <DropdownItem
                                            key={item.to}
                                            onClick={() => navigateTo(item.to)}
                                        >
                                            {item.label}
                                        </DropdownItem>
                                    ))}

                                    <DropdownItem onClick={handleLogout}>
                                        Log Out
                                    </DropdownItem>
                                </Dropdown>
                            </li>
                        </ul>
                    </nav>
                </div>

                {isMobileMenuOpen && (
                    <nav
                        className="logged-in-header__mobile-menu"
                        aria-label="Phone navigation"
                        data-testid="logged-in-header-mobile-menu"
                        style={{ maxHeight: mobileMenuMaxHeight }}
                    >
                        {PRIMARY_NAV_LINKS.map((link) => (
                            <Link key={link.to} to={link.to} onClick={closeDropdown}>
                                {link.label}
                            </Link>
                        ))}

                        {DROPDOWN_NAV_GROUPS.map((group) => (
                            <React.Fragment key={group.id}>
                                <p className="logged-in-header__mobile-menu-heading">{group.label}</p>
                                {group.items.map((item) => (
                                    <Link key={item.to} to={item.to} onClick={closeDropdown}>
                                        {item.label}
                                    </Link>
                                ))}
                            </React.Fragment>
                        ))}

                        <p className="logged-in-header__mobile-menu-heading">Account</p>
                        <div data-testid="logged-in-header-mobile-bricklink-status" className="logged-in-header__mobile-status">
                            <BrickLinkApiCounter />
                        </div>
                        <ThemeToggle showLabel />
                        {mobileMenuLinks.map((item) => (
                            <Link key={item.to} to={item.to} onClick={closeDropdown}>
                                {item.label}
                            </Link>
                        ))}
                        <button type="button" onClick={handleLogout}>
                            Log Out
                        </button>
                    </nav>
                )}
            </div>
        </>
    );
};

export default LoggedInHeader;
